import { SearchIcon } from 'components'
import { InputWithAdornment } from 'components/Form'
import { useBoolean, useClickOutside } from 'hooks'
import { useDebouncedSearch } from 'hooks/useDebouncedSearch'
import {
  useState,
  useCallback,
  ChangeEvent,
  useRef,
  useEffect,
  ChangeEventHandler,
  FocusEventHandler,
} from 'react'
import { Box } from 'theme-ui'
import { noop } from 'utils'
import { ResultsTray } from './ResultsTray'

export type GenericSearchProps = {
  id?: string
  name?: string
  setText?: string
  placeholder?: string
  autoFocus?: boolean
  limit?: number
  tabIndex?: number
  variant?: string
  searchFunc: ({ query }: { query: string }) => Promise<{ results: any[] }>
  onItemSelected: (item: any) => void
  ItemCard: ({
    item,
    onClick,
  }: {
    item: any
    onClick: () => void
  }) => JSX.Element
  filterResults?: (results: any[]) => any[]
  onChange?: ChangeEventHandler
  onBlur?: FocusEventHandler
  onError: (error: string) => void
}

export function GenericSearch({
  id,
  name,
  setText = '',
  placeholder = 'Search',
  autoFocus = false,
  limit = 5,
  tabIndex,
  variant = 'forms.genericSearch',
  searchFunc,
  onItemSelected,
  ItemCard,
  filterResults = (results) => results,
  onChange = noop,
  onBlur = noop,
  onError,
}: GenericSearchProps) {
  const [query, setQuery] = useState<string>('')

  useEffect(() => {
    setQuery(setText)
  }, [setText])

  const [results, search, state] = useDebouncedSearch(searchFunc)

  const updateSearch = useCallback(
    (value: string) => {
      setQuery(value)
      search({ query: value })
    },
    [setQuery, search]
  )

  function handleInputChange(evt: ChangeEvent<any>) {
    onChange(evt)
    const {
      target: { value },
    } = evt
    updateSearch(value)
  }

  const [showResults, openResultsTray, closeResultsTray] = useBoolean(false)
  const searchBoxRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  useClickOutside(searchBoxRef, closeResultsTray)

  const handleResultsTray = useCallback(
    (state) => {
      if (state.matches('idle')) {
        closeResultsTray()
      } else if (
        state.matches('ready.debouncing') ||
        state.matches('ready.searching') ||
        state.matches('ready.success')
      ) {
        openResultsTray()
      } else {
        closeResultsTray()
      }
    },
    [closeResultsTray, openResultsTray]
  )

  useEffect(() => {
    handleResultsTray(state)
  }, [state, handleResultsTray])

  useEffect(() => {
    if (state.matches('ready.failed')) {
      handleResultsTray(state)
      onError(state.context.error?.message ?? '')
    }
  }, [state, handleResultsTray, onError])

  const resetSearch = useCallback(() => {
    updateSearch('')
    onError('')
    searchInputRef.current?.focus()
  }, [updateSearch, onError])

  const cancelBtn = (
    <Box
      onClick={resetSearch}
      sx={{
        color: 'textPrimary',
        cursor: 'pointer',
        fontWeight: 600,
      }}>
      Cancel
    </Box>
  )
  const searchIcon = <SearchIcon />

  function handleFocus() {
    handleResultsTray(state)
  }

  const handleItemClick = (item: any) => {
    closeResultsTray()
    onItemSelected(item)
  }

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
      }}
      ref={searchBoxRef}>
      <InputWithAdornment
        id={id}
        name={name}
        autoFocus={autoFocus}
        variant={variant}
        placeholder={placeholder}
        aria-label="search-input"
        sx={{
          pl: query ? undefined : 7,
          pr: query ? '80px' : undefined,
        }}
        before={query ? null : searchIcon}
        after={query ? cancelBtn : null}
        isAfterClickable={query.length > 0}
        value={query}
        onFocus={handleFocus}
        onChange={handleInputChange}
        onBlur={onBlur}
        tabIndex={tabIndex}
        ref={searchInputRef}
      />
      <ResultsTray
        ItemCard={ItemCard}
        variant="search"
        results={results ? filterResults(results).slice(0, limit) : undefined}
        show={showResults}
        isLoading={
          state.matches('ready.debouncing') || state.matches('ready.searching')
        }
        onSearchItemClick={handleItemClick}
      />
    </Box>
  )
}
